import React, { useEffect, useRef } from 'react';
import { of } from 'rxjs';
import type { AnySchema } from 'yup';
import { SwarmType } from '@/graphql/types';
import { SWARM_TYPE_DATA_TYPES } from '../../../../../graph/constants';
import type { Graph } from '../../../../../graph/graph';
import { ValueFrequencyGraph, ValueTimeGraph } from '../../../../../graph/graph';
import VeffDayGraph from '../../../../../graph/veff-day-graph';
import type { Profile } from '../../../../../profile';
import { useProfile } from '../../../../../profile';
import { createDeferred } from '../../../../../utils/deferred';
import { BaseReportElement } from '../BaseReportElement';
import type {
    BuildStructureSettingsYupSchemaHelpers,
    ContextForKind,
    ElementContentRenderResult,
    ElementSettings,
    ElementSettingsFormSchema,
    GraphRuntimeState,
    RenderRuntime,
    ResolveElementSettingsFormSchemaArgs,
} from '../types';
import {
    DATA_TYPE_VALUES,
    createGraphDefinition,
    resolveReportBuilderLegendPlacement,
    type GraphSettings,
    type LegendPlacement,
    type SupportedGraphType,
} from './contracts';
import { getElementGraphRuntimeSettings } from './runtimeSettings';
import { createGraphSettingsFormSchema, normalizeGraphSettingsForSave } from './settingsForm';
import { buildGraphSettingsYupSchema, normalizeGraphSettings } from './settingsYupSchema';

const GRAPH_MARGIN = {
    top: 20,
    right: 0,
    bottom: 40,
    left: 55,
};

interface BaseGraphRendererProps {
    graphType: SupportedGraphType;
    graphRuntimeState: GraphRuntimeState;
    legendPlacement: LegendPlacement;
    width: number;
    height: number;
    onGraphMount: (graph: Graph) => () => void;
    onReady: () => void;
    onError: (error: unknown) => void;
}

function createGraphInstance(
    graphType: SupportedGraphType,
    graphRuntimeState: GraphRuntimeState,
    legendPlacement: LegendPlacement,
    container: HTMLDivElement,
    width: number,
    height: number,
    profile: Profile
): Graph {
    const baseGraphConfig = {
        margin: GRAPH_MARGIN,
        containerClass: 'report-builder-graph',
        legendPlacement,
        showTooltips: false,
        zoomTransitionDuration: 0,
        scales: graphRuntimeState.scales,
        legendData: graphRuntimeState.legendData,
        data: graphRuntimeState.dataSet,
        buildContainer: true,
        container,
        containerHeight: height,
        containerWidth: width,
        profile,
    };

    switch (graphType) {
        case 'velocity':
            return new ValueTimeGraph({
                ...baseGraphConfig,
                name: 'bar',
                graphType: graphRuntimeState.preferredGraphType,
                showBrushGraph: false,
            });
        case 'frequency':
            return new ValueFrequencyGraph({
                ...baseGraphConfig,
                name: 'dot',
                dotSize: 3,
                scaleType$: of(graphRuntimeState.frequencyGraphScaleType),
                containerExactSizeIsImportant: false,
            });
        case 'veff_day':
            return new VeffDayGraph({
                ...baseGraphConfig,
                name: 'veffDay',
            });
        default: {
            const unsupportedGraphType: never = graphType;
            throw new Error(
                `Graph type "${String(
                    unsupportedGraphType
                )}" does not have a Report Builder renderer.`
            );
        }
    }
}

function GraphRenderer({
    graphType,
    graphRuntimeState,
    legendPlacement,
    width,
    height,
    onGraphMount,
    onReady,
    onError,
}: BaseGraphRendererProps): JSX.Element {
    const profile = useProfile();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const onGraphMountRef = useRef(onGraphMount);
    const onReadyRef = useRef(onReady);
    const onErrorRef = useRef(onError);

    useEffect(() => {
        onGraphMountRef.current = onGraphMount;
    }, [onGraphMount]);

    useEffect(() => {
        onReadyRef.current = onReady;
    }, [onReady]);

    useEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);

    useEffect(() => {
        if (!containerRef.current) {
            return undefined;
        }
        const container = containerRef.current;
        const mountNode = document.createElement('div');
        container.replaceChildren(mountNode);

        let graph: Graph;
        try {
            graph = createGraphInstance(
                graphType,
                graphRuntimeState,
                legendPlacement,
                mountNode,
                width,
                height,
                profile
            );
        } catch (error) {
            mountNode.remove();
            onErrorRef.current(error);
            return undefined;
        }
        const unregisterGraph = onGraphMountRef.current(graph);

        let isActive = true;
        void graph.ready
            .then(() => {
                if (!isActive) {
                    return;
                }
                onReadyRef.current();
            })
            .catch((error) => {
                if (!isActive) {
                    return;
                }
                onErrorRef.current(error);
            });

        return () => {
            isActive = false;
            unregisterGraph();
            try {
                graph.unmount();
            } finally {
                mountNode.remove();
            }
        };
    }, [graphType, graphRuntimeState, legendPlacement, width, height, profile]);

    return <div ref={containerRef} />;
}

interface GraphReadySignal {
    ready: Promise<void>;
    resolve: () => void;
    reject: (error: unknown) => void;
}

function createGraphReadySignal(): GraphReadySignal {
    const deferred = createDeferred<void>();

    return {
        ready: deferred.promise,
        resolve: () => deferred.resolve(undefined),
        reject: (error) => deferred.reject(error),
    };
}

function renderGraphElement(
    settings: GraphSettings,
    ctx: ContextForKind<'measuring-point'>,
    runtime: RenderRuntime
): ElementContentRenderResult {
    const elementGraphSettings = getElementGraphRuntimeSettings(settings);
    const graphRuntimeState = runtime.getGraphRuntimeState(
        ctx.measuringPoint,
        elementGraphSettings
    );
    const graphReadySignal = createGraphReadySignal();

    return {
        node: (
            <div className="break-inside-avoid overflow-hidden">
                <GraphRenderer
                    graphType={settings.graphType}
                    graphRuntimeState={graphRuntimeState}
                    legendPlacement={resolveReportBuilderLegendPlacement(settings)}
                    width={settings.width}
                    height={settings.height}
                    onGraphMount={(graph) =>
                        runtime.registerRenderedGraph(
                            ctx.measuringPoint,
                            graph,
                            elementGraphSettings
                        )
                    }
                    onReady={graphReadySignal.resolve}
                    onError={graphReadySignal.reject}
                />
            </div>
        ),
        ready: Promise.all([graphRuntimeState.dataSet.ready, graphReadySignal.ready]).then(
            () => undefined
        ),
        hasRenderableContent: Promise.resolve(true),
    };
}

export class GraphElement extends BaseReportElement<'measuring-point', GraphSettings> {
    static readonly elementType = 'graph';

    static readonly layout = { flow: 'fixed' } as const;

    static readonly compatibleContextKinds = ['measuring-point'] as const;

    static readonly settingsFormSchemaDependencies = ['graphType', 'hiddenLinesMode'] as const;

    static get insertion() {
        return {
            label: gettext('ELEMENT_GRAPH_LABEL'),
            supportedSlots: ['child-list'] as const,
            createDefinition: () => createGraphDefinition(),
        };
    }

    static get settingsFormSchema(): ElementSettingsFormSchema {
        return createGraphSettingsFormSchema(undefined, DATA_TYPE_VALUES, true);
    }

    static resolveSettingsFormSchema({
        context,
        currentSettings,
    }: ResolveElementSettingsFormSchemaArgs): Promise<ElementSettingsFormSchema> {
        const dataTypes =
            context.kind === 'measuring-point'
                ? SWARM_TYPE_DATA_TYPES[context.measuringPoint.swarmType]
                : DATA_TYPE_VALUES;
        const includeEnabledAxesField =
            context.kind === 'project' || context.measuringPoint.swarmType === SwarmType.Vibration;

        return Promise.resolve(
            createGraphSettingsFormSchema(currentSettings, dataTypes, includeEnabledAxesField)
        );
    }

    static buildStructureSettingsYupSchema(
        _helpers: BuildStructureSettingsYupSchemaHelpers
    ): AnySchema {
        return buildGraphSettingsYupSchema();
    }

    static normalizeSettingsForSave(
        currentSettings: ElementSettings,
        nextSettings: ElementSettings,
        touchedSettingNames?: ReadonlySet<string>
    ): ElementSettings {
        return normalizeGraphSettingsForSave(currentSettings, nextSettings, touchedSettingNames);
    }

    protected normalizeSettings(settings: GraphSettings): GraphSettings {
        return normalizeGraphSettings(settings);
    }

    protected toCompatibleReactNode(
        ctx: ContextForKind<'measuring-point'>,
        runtime: RenderRuntime
    ): ElementContentRenderResult {
        return renderGraphElement(this.settings, ctx, runtime);
    }
}
